/**
 * Placements of the same data-bound field share one value.
 * Typing the signer's name in one box fills every other box bound to that key.
 */

const LINKABLE_TYPES = new Set(["TEXT", "DATE", "CHECKBOX"]);

export type LinkableSigningField = {
  id: string;
  type: string;
  dataKey?: string | null;
  filledByCreator?: boolean;
  locked?: boolean;
  value?: string | null;
};

export function signingDataKey(field: {
  dataKey?: string | null;
}): string | null {
  const key = field.dataKey?.trim();
  return key ? key : null;
}

function isEditableDataField(field: LinkableSigningField): boolean {
  if (!LINKABLE_TYPES.has(field.type)) return false;
  if (field.filledByCreator === true || field.locked === true) return false;
  return signingDataKey(field) != null;
}

/** Editable placements of one binding. Unbound or locked fields stay alone. */
export function linkedSigningFields<T extends LinkableSigningField>(
  field: T,
  fields: T[],
): T[] {
  const key = signingDataKey(field);
  if (!key || !isEditableDataField(field)) return [field];
  const group = fields.filter(
    (other) =>
      isEditableDataField(other) &&
      other.type === field.type &&
      signingDataKey(other) === key,
  );
  return group.length > 0 ? group : [field];
}

function nonEmpty(value: string | null | undefined): string | null {
  if (value == null) return null;
  return value.trim() !== "" ? value : null;
}

/**
 * The value shown and submitted for a field. Live edits win. When this
 * placement is blank, another editable placement of the same binding supplies it.
 */
export function sharedSigningValue(
  field: LinkableSigningField,
  fields: LinkableSigningField[],
  values: Record<string, string>,
): string {
  const group = linkedSigningFields(field, fields);
  const own = values[field.id];
  if (own != null && own.trim() !== "") return own;

  for (const member of group) {
    if (member.id === field.id) continue;
    const live = values[member.id];
    if (live != null && live.trim() !== "") return live;
  }

  if (group.some((member) => values[member.id] !== undefined)) return "";

  for (const member of group) {
    const stored = nonEmpty(
      member.value == null ? null : String(member.value),
    );
    if (stored) return stored;
  }
  return "";
}

/** Write one edit onto every editable placement of the same binding. */
export function fanOutSigningValue(
  fields: LinkableSigningField[],
  values: Record<string, string>,
  fieldId: string,
  value: string,
): Record<string, string> {
  const source = fields.find((field) => field.id === fieldId);
  const next = { ...values };
  if (!source) {
    next[fieldId] = value;
    return next;
  }
  for (const member of linkedSigningFields(source, fields)) {
    next[member.id] = value;
  }
  return next;
}

/**
 * Copy a known value onto blank placements of the same binding.
 * A live blank on every placement stays blank, so clearing one clears all.
 */
export function seedSharedSigningValues(
  fields: LinkableSigningField[],
  values: Record<string, string>,
): Record<string, string> {
  const next = { ...values };
  const seen = new Set<string>();
  for (const field of fields) {
    const group = linkedSigningFields(field, fields);
    const groupKey = group
      .map((member) => member.id)
      .sort()
      .join("|");
    if (seen.has(groupKey)) continue;
    seen.add(groupKey);
    if (group.length < 2) continue;

    const live = group
      .map((member) => next[member.id])
      .filter((value): value is string => value !== undefined);
    if (live.length > 0 && live.every((value) => value.trim() === "")) {
      continue;
    }

    let shared: string | null = null;
    for (const member of group) {
      shared = nonEmpty(next[member.id]);
      if (shared) break;
    }
    if (!shared) {
      for (const member of group) {
        shared = nonEmpty(
          member.value == null ? null : String(member.value),
        );
        if (shared) break;
      }
    }
    if (!shared) continue;
    for (const member of group) {
      if (!nonEmpty(next[member.id])) next[member.id] = shared;
    }
  }
  return next;
}
